import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TrendingUp,
  Users,
  Settings,
  AlertCircle,
  Download,
  RefreshCw,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const SettingsAnalytics = ({ currentUser }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSetting, setSelectedSetting] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/manager/settings-analytics`,
        { withCredentials: true }
      );
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!analytics) return;

    const exportData = {
      generated_at: new Date().toISOString(),
      customization_rate: `${analytics.customization_rate}%`,
      total_team_members: analytics.total_team_members,
      members_with_customizations: analytics.members_with_customizations,
      most_overridden_settings: analytics.most_overridden_settings,
      settings_distributions: analytics.settings_distributions
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `settings-analytics-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-blue-500 animate-spin mr-2" />
        <span className="text-gray-600">Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-800 font-medium">Error Loading Analytics</h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <button
              onClick={loadAnalytics}
              className="mt-2 text-sm text-red-700 hover:text-red-900 underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Settings Analytics</h2>
          <p className="text-sm text-gray-600 mt-1">
            Team settings usage and customization patterns
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadAnalytics}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          icon={Users}
          label="Total Team Members"
          value={analytics.total_team_members}
          color="blue"
        />
        <StatCard
          icon={Settings}
          label="Members with Customizations"
          value={analytics.members_with_customizations}
          color="green"
        />
        <StatCard
          icon={TrendingUp}
          label="Customization Rate"
          value={`${analytics.customization_rate.toFixed(1)}%`}
          color="purple"
        />
      </div>

      {/* Customization Rate Visual */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <PieChart className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">Team Customization Overview</h3>
        </div>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700">Using Custom Settings</span>
              <span className="font-medium text-green-600">
                {analytics.members_with_customizations} members
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${analytics.customization_rate}%` }}
              ></div>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700">Using Org Defaults</span>
              <span className="font-medium text-gray-600">
                {analytics.total_team_members - analytics.members_with_customizations} members
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gray-400 h-3 rounded-full transition-all duration-500"
                style={{ width: `${100 - analytics.customization_rate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Most Overridden Settings */}
      {analytics.most_overridden_settings && analytics.most_overridden_settings.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-gray-700" />
            <h3 className="text-lg font-semibold text-gray-900">Most Overridden Settings</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Settings that team members customize most frequently
          </p>
          <div className="space-y-3">
            {analytics.most_overridden_settings.map((stat, idx) => (
              <div
                key={stat.setting_key}
                className="cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                onClick={() => setSelectedSetting(
                  selectedSetting === stat.setting_key ? null : stat.setting_key
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {idx + 1}. {formatFieldName(stat.setting_key)}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({stat.override_count} customizations)
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-blue-600">
                    {(stat.percentage * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${stat.percentage * 100}%` }}
                  ></div>
                </div>

                {/* Show distribution when selected */}
                {selectedSetting === stat.setting_key &&
                  analytics.settings_distributions[stat.setting_key] && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <SettingDistribution
                        field={stat.setting_key}
                        distribution={analytics.settings_distributions[stat.setting_key]}
                      />
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Settings Distributions */}
      {analytics.settings_distributions && Object.keys(analytics.settings_distributions).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-gray-700" />
            <h3 className="text-lg font-semibold text-gray-900">All Settings Distributions</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Click on a setting in "Most Overridden Settings" above to see its value distribution
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(analytics.settings_distributions).map(([field, distribution]) => (
              <div
                key={field}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
              >
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  {formatFieldName(field)}
                </h4>
                <SettingDistribution field={field} distribution={distribution} compact />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights and Recommendations */}
      <AnalyticsInsights analytics={analytics} />
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

const SettingDistribution = ({ field, distribution, compact = false }) => {
  if (!distribution || !distribution.values || distribution.values.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No distribution data available
      </div>
    );
  }

  const maxCount = Math.max(...distribution.values.map(v => v.count));

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 font-medium">
        <div>Value</div>
        <div className="text-right">Count</div>
        <div className="text-right">%</div>
      </div>
      {distribution.values.map((valueData, idx) => {
        const percentage = (valueData.count / distribution.total_users) * 100;
        const barWidth = (valueData.count / maxCount) * 100;

        return (
          <div key={idx} className="space-y-1">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="font-medium text-gray-900 truncate" title={String(valueData.value)}>
                {formatValue(field, valueData.value)}
              </div>
              <div className="text-right text-gray-700">{valueData.count}</div>
              <div className="text-right text-gray-700">{percentage.toFixed(1)}%</div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${barWidth}%` }}
              ></div>
            </div>
          </div>
        );
      })}
      {distribution.mean !== null && (
        <div className="pt-2 border-t border-gray-200 text-xs text-gray-600">
          <div className="flex justify-between">
            <span>Average:</span>
            <span className="font-medium">{formatValue(field, distribution.mean)}</span>
          </div>
          {distribution.min !== null && distribution.max !== null && (
            <div className="flex justify-between mt-1">
              <span>Range:</span>
              <span className="font-medium">
                {formatValue(field, distribution.min)} - {formatValue(field, distribution.max)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AnalyticsInsights = ({ analytics }) => {
  const insights = [];

  // High customization rate
  if (analytics.customization_rate > 70) {
    insights.push({
      type: 'warning',
      title: 'High Customization Rate',
      message: `${analytics.customization_rate.toFixed(1)}% of your team has customized settings. Consider updating organization defaults to better match common practices.`
    });
  } else if (analytics.customization_rate < 20) {
    insights.push({
      type: 'info',
      title: 'Low Customization Rate',
      message: `Only ${analytics.customization_rate.toFixed(1)}% of your team uses custom settings. Your organization defaults appear to work well for most members.`
    });
  }

  // Most overridden settings
  if (analytics.most_overridden_settings && analytics.most_overridden_settings.length > 0) {
    const topSetting = analytics.most_overridden_settings[0];
    if (topSetting.percentage * 100 > 60) {
      insights.push({
        type: 'warning',
        title: 'Frequently Overridden Setting',
        message: `${(topSetting.percentage * 100).toFixed(1)}% of customizing users override "${formatFieldName(topSetting.setting_key)}". Consider creating a template or adjusting the organization default.`
      });
    }
  }

  // Wide distribution
  if (analytics.settings_distributions) {
    Object.entries(analytics.settings_distributions).forEach(([field, dist]) => {
      if (dist.values && dist.values.length >= 5) {
        insights.push({
          type: 'info',
          title: 'Diverse Settings Usage',
          message: `"${formatFieldName(field)}" has ${dist.values.length} different values across your team, suggesting diverse territory needs.`
        });
      }
    });
  }

  if (insights.length === 0) {
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <AlertCircle className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-blue-900">Insights & Recommendations</h3>
      </div>
      <div className="space-y-3">
        {insights.slice(0, 3).map((insight, idx) => (
          <div
            key={idx}
            className={`p-4 rounded-lg ${
              insight.type === 'warning'
                ? 'bg-yellow-50 border border-yellow-200'
                : 'bg-white border border-blue-200'
            }`}
          >
            <h4
              className={`text-sm font-semibold mb-1 ${
                insight.type === 'warning' ? 'text-yellow-900' : 'text-blue-900'
              }`}
            >
              {insight.title}
            </h4>
            <p
              className={`text-sm ${
                insight.type === 'warning' ? 'text-yellow-800' : 'text-blue-800'
              }`}
            >
              {insight.message}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to format field names
const formatFieldName = (field) => {
  if (!field) return '';
  return field
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to format values
const formatValue = (field, value) => {
  if (value === null || value === undefined) return 'N/A';

  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Handle numeric values with units
  if (field.includes('_km')) {
    return `${Number(value).toFixed(1)} km`;
  }
  if (field.includes('_kmh')) {
    return `${Number(value).toFixed(0)} km/h`;
  }
  if (field.includes('days')) {
    return `${Number(value).toFixed(0)} days`;
  }
  if (field.includes('factor')) {
    return `${Number(value).toFixed(2)}x`;
  }
  if (field.includes('weight')) {
    return `${Number(value).toFixed(2)}`;
  }
  if (field.includes('threshold') && typeof value === 'number') {
    return `$${Number(value).toLocaleString()}`;
  }

  // Default formatting
  if (typeof value === 'number') {
    return Number(value).toFixed(2);
  }

  return String(value);
};

export default SettingsAnalytics;
